import {
    fetchAllAddressRegisterList,
    downloadAddressRegister,
    downloadSampleCsvForCreate,
    downloadSampleCsvForUpdate,
    uploadCsvForCreate,
    uploadCsvForUpdate,
    createAddressRegister,
    updateAddressRegister,
    getAddressDetail,
} from '@api/addressRegister';

import {
    csvKeyMapping,
    formFieldList,
    getCustomFieldList,
    getDynamicCsvKeyMapping,
} from '@components/pages/OpsDashboard/Setup/AddressRegisterModule/utils';

// Generic resolver: options for a config-enum field come from a named key under
// ops_dashboard_config.config (e.g. valid_constraint_tags — the same optimiser-tag
// vocabulary the rest of the app reads from). Reusable for any future
// "pick N from a config-provided list" address field.
export const getConfigEnumOptions = (masterdata, configKey) =>
    (masterdata?.ops_dashboard_config?.config?.[configKey] || []).map((value) => ({
        label: value,
        value,
    }));

const getSingleUploadFields = (masterdata, customFields = []) => {
    const masterFieldsList = masterdata?.location_master_config?.fields_list || [];
    const sections = formFieldList.map((section) =>
        section.id === 'basic_details'
            ? { ...section, fields_list: [...section.fields_list, ...getCustomFieldList(customFields)] }
            : section);
    const fieldsListToShow = [];
    for (const section of sections) {
        const obj = {};
        obj['id'] = section.id;
        obj['name'] = section.name;
        obj['fields_list'] = [];
        const fieldList = section?.fields_list || [];
        for (const field of fieldList) {
            const foundField = masterFieldsList.find((masterField) => masterField.key === field.id);
            if (foundField) {
                field['isMandatory'] = !foundField.is_optional;
                // Config-enum fields declare optionsConfigKey; resolve their options
                // from masterData (the only enrichment beyond isMandatory).
                const optionsConfigKey = (field as any).optionsConfigKey;
                if (optionsConfigKey) {
                    (field as any).options = getConfigEnumOptions(masterdata, optionsConfigKey);
                }
                obj['fields_list'].push(field);
            }
        }
        // Only add section if it has at least one field to show
        if (obj['fields_list'].length > 0) {
            fieldsListToShow.push(obj);
        }
    }
    return fieldsListToShow;
};

const getKeys = (masterdata) => {
    const masterFieldsList = masterdata?.location_master_config?.fields_list || [];
    return masterFieldsList?.map((field) => field.key);
}

const viewControl = {
    pageTitle: 'Setup',
    buckets: ['addressRegister'],
    globalActions: [],
    filters: [
        'address_id', 'verification_status', 'city',
    ],
    actions: [
        {
            key: 'download',
            name: 'Download',
        },
        {
            key: 'update',
            name: 'Bulk Update',
        },
        {
            key: 'addOptions',
            name: 'Add New',
        },
        {
            key: 'pagination',
            name: 'Pagination',
        },
        {
            key: 'perPageCountChange',
            name: 'Pagination Count',
        },
        {
            key: 'reload',
            name: 'Reload',
        },
    ],
    addOptions: [
        {
            key: 'add_single',
            title: 'Single',
            action: 'add_single',
            type: 'single',
        },
        {
            key: 'add_bulk',
            title: 'Bulk',
            action: 'add_bulk',
            type: 'bulk',
        },
    ],
    fetchData: fetchAllAddressRegisterList,
    downloadData: downloadAddressRegister,
    add_bulk: {
        sampleUrl: undefined,
        downloadSampleCsvFromApiCall: true,
        downloadSampleCsv: downloadSampleCsvForCreate,
        title: 'Add Address',
        onBulkUpload: uploadCsvForCreate,
    },
    update: {
        sampleUrl: undefined,
        downloadSampleCsvFromApiCall: true,
        downloadSampleCsv: downloadSampleCsvForUpdate,
        title: 'Update Address',
        onBulkUpload: uploadCsvForUpdate,
    },
    params: 'address_array',
    firstRowAsHeader: true,
    modalHeading: 'Upload Address',
    warningMsg: 'No Address to upload',
    successMsg: 'Uploaded Successfully',
    updateMsg: 'Updated Successfully',
    failureArrayColumns: [
        {
            key: 'row_num',
            dataIndex: 'row_num',
            title: 'S No',
        },
        {
            key: 'message',
            dataIndex: 'message',
            title: 'Reason',
        },
    ],
    bulkUploadMapping: null,
    headerMapping: (masterdata, customFields = []) => {
        const dynamicMapping = getDynamicCsvKeyMapping(masterdata, customFields);
        const keyToLabel = Object.keys(dynamicMapping).length ? dynamicMapping : csvKeyMapping;
        return Object.fromEntries(
            Object.entries(keyToLabel).map(([key, label]) => [String(label).trim(), key])
        );
    },
    sectionWiseForm: true,
    sectionLayout: 'vertical',
    singleUploadSectionWiseField: getSingleUploadFields,
    s3FolderDocumentTypeMapping: {
        location_image: 'location_image',
    },
    onSingleAdd: createAddressRegister,
    onSingleUpdate: updateAddressRegister,
    id: 'address_id',
    getAddressDetail: getAddressDetail,
    getFormKeys: getKeys,
};

const buckets = {
    addressRegister: {
        name: 'Address Register',
        filters: [],
        actions: [],
    },
};

const bucketColor = {};

export default { viewControl, buckets, bucketColor };
